// apps/web/src/api/cargo.ts — typed wrappers around `/api/cargo/*`
// (#1228/#1230 backend). All callers route through the shared `request<T>`
// so the coordinated 401-retry / `ApiClientError` surface is preserved.
//
// MVP (#1231) reads only: the list and the summary. The registry Carrier filter
// is a free-text input (#1945): the `carrier=` query param is an open string
// (trimmed, exact-match), not a closed enum, so no dictionary fetch is needed.
// Write/import/payment routes are deferred to #1232.

import {
  CargoAssignableManagersResponseSchema,
  CargoFilterOptionsResponseSchema,
  CargoListResponseSchema,
  CargoStatisticsResponseSchema,
  CargoSummaryResponseSchema,
  type CargoAssignableManagersResponse,
  type CargoFilterOptionsResponse,
  type CargoListQuery,
  type CargoListResponse,
  type CargoStatisticsResponse,
  type CargoSummaryResponse,
} from '@magimp/shared';

import { request } from './client.js';

function withQuery(path: string, qs: string): string {
  return qs.length > 0 ? `${path}?${qs}` : path;
}

/**
 * @aiPurpose serialize the cargo list/summary filters into a URL query
 *   string. Surface mirrors the optional keys of `CargoListQuerySchema`.
 *   `dateType` is only emitted when a date range is actually applied — the
 *   backend defaults it to `china_ship`, so sending it bare would pin a
 *   filter the user never chose.
 */
export function serializeCargoFilters(filters: Partial<CargoListQuery> = {}): string {
  const sp = new URLSearchParams();
  if (filters.q) sp.set('q', filters.q);
  if (filters.responsibleManagerId) sp.set('responsibleManagerId', filters.responsibleManagerId);
  if (filters.carrier) sp.set('carrier', filters.carrier);
  if (filters.pathFormat) sp.set('pathFormat', filters.pathFormat);
  if (filters.clientPaymentStatus) sp.set('clientPaymentStatus', filters.clientPaymentStatus);
  if (filters.carrierPaymentStatus) sp.set('carrierPaymentStatus', filters.carrierPaymentStatus);
  if (filters.lifecycle) sp.set('lifecycle', filters.lifecycle);
  if (filters.dateFrom) sp.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) sp.set('dateTo', filters.dateTo);
  // WHY: only meaningful alongside a range — see fn doc above.
  if ((filters.dateFrom || filters.dateTo) && filters.dateType) {
    sp.set('dateType', filters.dateType);
  }
  if (filters.weightFrom !== undefined) sp.set('weightFrom', String(filters.weightFrom));
  if (filters.weightTo !== undefined) sp.set('weightTo', String(filters.weightTo));
  if (filters.volumeFrom !== undefined) sp.set('volumeFrom', String(filters.volumeFrom));
  if (filters.volumeTo !== undefined) sp.set('volumeTo', String(filters.volumeTo));
  if (filters.totalCostFrom !== undefined) sp.set('totalCostFrom', String(filters.totalCostFrom));
  if (filters.totalCostTo !== undefined) sp.set('totalCostTo', String(filters.totalCostTo));
  if (filters.overdue) sp.set('overdue', filters.overdue);
  // #1939: only `true` narrows; omit otherwise so a fresh query stays clean.
  if (filters.hasRejectedDocument) sp.set('hasRejectedDocument', 'true');
  if (filters.cursor) sp.set('cursor', filters.cursor);
  if (filters.limit !== undefined) sp.set('limit', String(filters.limit));
  return sp.toString();
}

export function listCargo(
  filters: Partial<CargoListQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<CargoListResponse> {
  return request<CargoListResponse>(
    withQuery('/cargo', serializeCargoFilters(filters)),
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CargoListResponseSchema,
  );
}

export function fetchCargoSummary(
  filters: Partial<CargoListQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<CargoSummaryResponse> {
  return request<CargoSummaryResponse>(
    withQuery('/cargo/summary', serializeCargoFilters(filters)),
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CargoSummaryResponseSchema,
  );
}

export function fetchCargoFilterOptions(
  filters: Partial<CargoListQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<CargoFilterOptionsResponse> {
  return request<CargoFilterOptionsResponse>(
    withQuery('/cargo/filter-options', serializeCargoFilters(filters)),
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CargoFilterOptionsResponseSchema,
  );
}

// #2261: the responsible-manager roster for the Add/Edit modal assignment
// select. Sourced from the users directory (not cargo rows), so the endpoint
// takes no filters — the registry FILTER keeps using `fetchCargoFilterOptions`.
export function fetchCargoAssignableManagers(
  opts: { signal?: AbortSignal } = {},
): Promise<CargoAssignableManagersResponse> {
  return request<CargoAssignableManagersResponse>(
    '/cargo/assignable-managers',
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CargoAssignableManagersResponseSchema,
  );
}

export function fetchCargoStatistics(
  filters: Partial<CargoListQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<CargoStatisticsResponse> {
  return request<CargoStatisticsResponse>(
    withQuery('/cargo/statistics', serializeCargoFilters(filters)),
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    CargoStatisticsResponseSchema,
  );
}
